import React, { useId, useMemo } from "react";
import { Box, Collapse, IconButton, Tooltip, Typography, alpha } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingFlatRoundedIcon from "@mui/icons-material/TrendingFlatRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { REVIEW_STATUSES } from "../AuditHub/constants";
import { formatDateTime, scorePercent } from "../AuditHub/utils";
import { AH_ACCENT, AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK, AH_WHITE, MetaSeparator, cardShellSx } from "../AuditHub/theme";
import {
  CardHeader,
  CardShell,
  FAILED_COLOR,
  FAILED_TEXT_COLOR,
  PASSED_COLOR,
  PASSED_TEXT_COLOR,
  Tile,
  formatCount,
  labelSx,
} from "./AuditCardParts";
import { readComponents } from "./auditMetrics";

const PENDING_SCAN = "Pending automated scan completion";
const accountGridSx = {
  display: "grid",
  gap: 1,
  alignItems: "stretch",
  gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(200px, 0.85fr) minmax(0, 2fr)" },
};

const sideStackSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
  height: { md: "100%" },
};

const tileFillSx = { height: "100%" };

const compactTileSx = { px: 1.25, py: 0.75, gap: 0.5 };

const Quiet = ({ children }) => (
  <Typography sx={{ fontSize: 13, color: AH_MUTED, overflowWrap: "anywhere" }}>{children}</Typography>
);

const metricRowSx = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  columnGap: 1,
  alignItems: "baseline",
};

const metricHeadSx = {
  fontSize: 13,
  fontWeight: 600,
  textAlign: "center",
  lineHeight: 1.3,
};

const metricValueSx = {
  fontSize: 16,
  fontWeight: 700,
  color: AH_TEXT,
  textAlign: "center",
  lineHeight: 1.2,
  fontVariantNumeric: "tabular-nums",
};

/** Change from the automated baseline, shown at the top right of the compliance card. */
const ScoreDelta = ({ score, baseline }) => {
  if (!Number.isFinite(score) || !Number.isFinite(baseline)) return null;
  const delta = score - baseline;
  const flat = Math.abs(delta) < 0.005;
  const up = delta > 0;
  const tone = flat
    ? { color: AH_MUTED, bg: AH_TRACK, Icon: TrendingFlatRoundedIcon, text: "No change" }
    : up
      ? { color: PASSED_TEXT_COLOR, bg: "#f0fdf4", Icon: TrendingUpRoundedIcon, text: `+${delta.toFixed(2)}%` }
      : { color: FAILED_TEXT_COLOR, bg: "#fef2f2", Icon: TrendingDownRoundedIcon, text: `${delta.toFixed(2)}%` };
  return (
    <Tooltip title="Change from the automated baseline" arrow placement="top">
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: 99,
          bgcolor: tone.bg,
          color: tone.color,
          flexShrink: 0,
        }}
      >
        <tone.Icon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "inherit", whiteSpace: "nowrap" }}>{tone.text}</Typography>
      </Box>
    </Tooltip>
  );
};

const isStatusMap = (value) =>
  Boolean(value) &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  REVIEW_STATUSES.some((status) => Object.prototype.hasOwnProperty.call(value, status.value));

/** Review-status bars are this account's counts. The assessment map covers every account. */
const statusCountsForAccount = (audit, accountNumber) => {
  if (!accountNumber) return null;
  const accounts = Array.isArray(audit?.accounts) ? audit.accounts : [];
  const account = accounts.find((item) => String(item?.accountNumber) === String(accountNumber));
  return isStatusMap(account?.statusCounts) ? account.statusCounts : null;
};

/** Every review status on the map, including zeros. */
const reviewRows = (statusCounts) => {
  if (!statusCounts) return [];
  return REVIEW_STATUSES.map((status) => {
    const count = Number(statusCounts[status.value]);
    return { ...status, count: Number.isFinite(count) && count > 0 ? count : 0 };
  });
};

const dash = "—";

const DONUT = {
  width: 340,
  height: 228,
  cx: 170,
  cy: 114,
  outer: 90,
  inner: 58,
};

/** Clockwise degrees from 12 o'clock, matching a standard donut. */
const pointOn = (degrees, radius) => {
  const rad = ((degrees - 90) * Math.PI) / 180;
  return {
    x: DONUT.cx + radius * Math.cos(rad),
    y: DONUT.cy + radius * Math.sin(rad),
  };
};

const ringSlice = (start, sweep) => {
  if (sweep >= 359.99) {
    const { outer, inner, cx, cy } = DONUT;
    return [
      `M ${cx - outer} ${cy}`,
      `A ${outer} ${outer} 0 1 1 ${cx + outer} ${cy}`,
      `A ${outer} ${outer} 0 1 1 ${cx - outer} ${cy}`,
      `M ${cx - inner} ${cy}`,
      `A ${inner} ${inner} 0 1 0 ${cx + inner} ${cy}`,
      `A ${inner} ${inner} 0 1 0 ${cx - inner} ${cy}`,
      "Z",
    ].join(" ");
  }
  const end = start + sweep;
  const large = sweep > 180 ? 1 : 0;
  const outerStart = pointOn(start, DONUT.outer);
  const outerEnd = pointOn(end, DONUT.outer);
  const innerEnd = pointOn(end, DONUT.inner);
  const innerStart = pointOn(start, DONUT.inner);
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${DONUT.outer} ${DONUT.outer} 0 ${large} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${DONUT.inner} ${DONUT.inner} 0 ${large} 0 ${innerStart.x} ${innerStart.y}`,
    "Z",
  ].join(" ");
};

/** Each marker sits on the midpoint of its own slice, so it moves as the counts change. */
const scoreSlices = (passed, failed) => {
  const total = passed + failed;
  const slices = [
    { key: "passed", name: "Passed", count: passed, fill: PASSED_COLOR, ink: PASSED_TEXT_COLOR },
    { key: "failed", name: "Failed", count: failed, fill: FAILED_COLOR, ink: FAILED_TEXT_COLOR },
  ].filter((slice) => slice.count > 0);
  if (!total || slices.length === 0) return [];
  if (slices.length === 1) {
    return [{ ...slices[0], start: 0, sweep: 360, mid: 0 }];
  }
  let cursor = 0;
  return slices.map((slice) => {
    const sweep = (slice.count / total) * 360;
    const gap = sweep > 10 ? 2.4 : 0;
    const mid = cursor + sweep / 2;
    const drawn = { ...slice, start: cursor + gap / 2, sweep: Math.max(sweep - gap, 0.4), mid };
    cursor += sweep;
    return drawn;
  });
};

const markerFor = (mid) => {
  const side = Math.sin((mid * Math.PI) / 180);
  const anchor = side > 0.4 ? "start" : side < -0.4 ? "end" : "middle";
  const lineEnd = pointOn(mid, DONUT.outer + 8);
  const labelAt = pointOn(mid, DONUT.outer + (anchor === "middle" ? 28 : 16));
  return { anchor, lineStart: pointOn(mid, DONUT.outer + 2), lineEnd, labelAt };
};

const LABEL_FONT = 18;
const sliceLabel = (slice) => `${slice.name} ${formatCount(slice.count)}`;

/** Crop to the ring and the labels that are actually drawn, so the card has no empty frame. */
const viewBoxFor = (slices) => {
  const pad = 6;
  let minX = DONUT.cx - DONUT.outer;
  let maxX = DONUT.cx + DONUT.outer;
  let minY = DONUT.cy - DONUT.outer;
  let maxY = DONUT.cy + DONUT.outer;
  slices.forEach((slice) => {
    const marker = markerFor(slice.mid);
    const width = sliceLabel(slice).length * (LABEL_FONT * 0.64);
    const left = marker.anchor === "start" ? marker.labelAt.x : marker.anchor === "end" ? marker.labelAt.x - width : marker.labelAt.x - width / 2;
    minX = Math.min(minX, left);
    maxX = Math.max(maxX, left + width);
    minY = Math.min(minY, marker.labelAt.y - LABEL_FONT / 2);
    maxY = Math.max(maxY, marker.labelAt.y + LABEL_FONT / 2);
  });
  return `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}`;
};

/** Fill most of the hole. Wider scores step down so 100.00% still clears the ring. */
const scoreFontSize = (label) => {
  if (label === dash) return 28;
  const targetWidth = DONUT.inner * 2 * 0.9;
  return Math.max(24, Math.min(40, targetWidth / (label.length * 0.62)));
};

/** Pass is the green arc and fail is the red arc. Markers follow each arc. The score sits in the hole. */
const ScoreDonut = ({ score, passed, failed, blank }) => {
  const empty = blank || passed + failed <= 0;
  const slices = empty ? [] : scoreSlices(passed, failed);
  const label = Number.isFinite(score) ? `${score.toFixed(2)}%` : dash;
  return (
    <Box sx={{ width: "100%", maxWidth: 268, mx: "auto" }}>
      <svg
        viewBox={viewBoxFor(slices)}
        role="img"
        aria-label={empty ? "Compliance score unavailable" : `Compliance score ${label}. Passed ${passed}, failed ${failed}.`}
        style={{ display: "block", width: "100%", height: "auto" }}
      >
        {empty ? (
          <path d={ringSlice(0, 360)} fill={AH_TRACK} />
        ) : (
          slices.map((slice) => <path key={slice.key} d={ringSlice(slice.start, slice.sweep)} fill={slice.fill} />)
        )}
        {!empty &&
          slices.map((slice) => {
            const marker = markerFor(slice.mid);
            return (
              <g key={`${slice.key}-marker`}>
                <line
                  x1={marker.lineStart.x}
                  y1={marker.lineStart.y}
                  x2={marker.lineEnd.x}
                  y2={marker.lineEnd.y}
                  stroke={AH_MUTED}
                  strokeWidth="1.5"
                />
                <text
                  x={marker.labelAt.x}
                  y={marker.labelAt.y}
                  textAnchor={marker.anchor}
                  dominantBaseline="middle"
                  fill={slice.ink}
                  fontSize={LABEL_FONT}
                  fontWeight="700"
                  fontFamily="inherit"
                >
                  {`${slice.name} ${formatCount(slice.count)}`}
                </text>
              </g>
            );
          })}
        <text
          x={DONUT.cx}
          y={DONUT.cy}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={AH_TEXT}
          fontSize={scoreFontSize(label)}
          fontWeight="700"
          fontFamily="inherit"
        >
          {label}
        </text>
      </svg>
    </Box>
  );
};

export const ComplianceScoreCard = ({ score, baseline, passed, failed, known, unavailable, pendingScan }) => {
  const countsBlank = !known || (pendingScan && passed + failed === 0);
  return (
    <Tile label="Compliance Score" action={<ScoreDelta score={score} baseline={baseline} />} align="center" sx={{ ...tileFillSx, ...compactTileSx }}>
      {!known ? (
        <Quiet>{unavailable || dash}</Quiet>
      ) : (
        <>
          <ScoreDonut score={score} passed={passed} failed={failed} blank={countsBlank} />
          {pendingScan && !Number.isFinite(score) ? <Quiet>{PENDING_SCAN}</Quiet> : null}
        </>
      )}
    </Tile>
  );
};

const COLUMNS = ["total", "passed", "failed"];

const MetricRow = ({ cells }) => (
  <Box sx={metricRowSx}>
    {cells.map((cell, index) => (
      <Typography key={COLUMNS[index]} sx={metricValueSx}>
        {cell}
      </Typography>
    ))}
  </Box>
);

const AutomatedControlsCard = ({ passed, failed, known, pendingScan, resources }) => {
  const total = passed + failed;
  const resourceTotal = Number(resources?.total) || 0;
  const resourcePassed = Number(resources?.passed) || 0;
  const resourceFailed = Number(resources?.failed) || 0;
  const controlsPending = pendingScan && total === 0;
  const resourcesMissing = resources == null;
  if (!known) {
    return (
      <Tile label="Automated Controls" align="start" sx={compactTileSx}>
        <Quiet>{dash}</Quiet>
      </Tile>
    );
  }
  if (controlsPending && resourcesMissing) {
    return (
      <Tile label="Automated Controls" align="start" sx={compactTileSx}>
        <Quiet>{PENDING_SCAN}</Quiet>
      </Tile>
    );
  }
  const controlCells = controlsPending ? [dash, dash, dash] : [formatCount(total), formatCount(passed), formatCount(failed)];
  const passedPercent = resourceTotal > 0 ? (resourcePassed / resourceTotal) * 100 : 0;
  const halfSx = {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 0.75,
  };
  return (
    <Box sx={{ ...cardShellSx, ...compactTileSx, display: "flex", flexDirection: "column", width: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
          alignItems: "center",
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box sx={{ ...halfSx, minWidth: 0, pr: { sm: 2 }, overflow: "hidden" }}>
          <Typography noWrap sx={labelSx}>Automated Controls</Typography>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ ...metricRowSx, width: "100%", "& > *": { minWidth: 0 } }}>
              <Typography noWrap sx={{ ...metricHeadSx, color: AH_MUTED }}>Total</Typography>
              <Typography noWrap sx={{ ...metricHeadSx, color: PASSED_TEXT_COLOR }}>Passed</Typography>
              <Typography noWrap sx={{ ...metricHeadSx, color: FAILED_TEXT_COLOR }}>Failed</Typography>
            </Box>
            <Box sx={{ ...metricRowSx, width: "100%", "& > *": { minWidth: 0 } }}>
              {controlCells.map((cell, index) => (
                <Typography key={COLUMNS[index]} noWrap sx={metricValueSx}>
                  {cell}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            ...halfSx,
            minWidth: 0,
            overflow: "hidden",
            borderTop: { xs: `1px solid ${AH_BORDER}`, sm: "none" },
            borderLeft: { sm: `1px solid ${AH_BORDER}` },
            pt: { xs: 1.25, sm: 0 },
            pl: { sm: 2 },
            mt: { xs: 1.25, sm: 0 },
          }}
        >
          {resourcesMissing ? (
            <Quiet>{pendingScan ? PENDING_SCAN : dash}</Quiet>
          ) : (
            <>
              <Typography noWrap sx={labelSx}>Resource Scanned (Automated): {formatCount(resourceTotal)}</Typography>
              <Tooltip title={`Passed: ${formatCount(resourcePassed)} | Failed: ${formatCount(resourceFailed)}`} arrow placement="top">
                <Box sx={{ height: 7, width: "100%", borderRadius: 5, overflow: "hidden", display: "flex", bgcolor: FAILED_COLOR }}>
                  <Box sx={{ width: `${passedPercent}%`, bgcolor: PASSED_COLOR }} />
                </Box>
              </Tooltip>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: PASSED_TEXT_COLOR, whiteSpace: "nowrap" }}>
                  {formatCount(resourcePassed)} Passed
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: FAILED_TEXT_COLOR, whiteSpace: "nowrap" }}>
                  {formatCount(resourceFailed)} Failed
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ManualControlsCard = ({ components, known, review }) => {
  const total = components.manualPassed + components.manualFailed + components.notApplicable + components.undecided;
  const maxReview = Math.max(...review.map((item) => item.count), 1);
  const shellSx = {
    ...cardShellSx,
    ...compactTileSx,
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  };
  if (!known || (total <= 0 && !review.length)) {
    return (
      <Box sx={shellSx}>
        <Typography noWrap sx={labelSx}>Manual Controls</Typography>
        <Quiet>{!known ? dash : "No manual controls"}</Quiet>
      </Box>
    );
  }
  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          display: "grid",
          flex: 1,
          alignItems: "stretch",
          minWidth: 0,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: total > 0 && review.length ? "max-content minmax(0, 1fr)" : "minmax(0, 1fr)",
          },
        }}
      >
        {total > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0, alignItems: "center", pr: review.length ? "10px" : 0 }}>
            <Typography noWrap sx={{ ...labelSx, alignSelf: "flex-start" }}>Manual Controls</Typography>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
              <Typography noWrap sx={{ ...metricHeadSx, color: AH_MUTED }}>Total</Typography>
              <Typography noWrap sx={{ ...metricValueSx, fontSize: 28, lineHeight: 1.1 }}>{formatCount(total)}</Typography>
            </Box>
          </Box>
        ) : null}
        {review.length ? (
          <Box
            sx={{
              minWidth: 0,
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              borderTop: { xs: total > 0 ? `1px solid ${AH_BORDER}` : "none", sm: "none" },
              borderLeft: { sm: total > 0 ? `1px solid ${AH_BORDER}` : "none" },
              pt: { xs: total > 0 ? 1 : 0, sm: 0 },
              pl: { xs: 0, sm: total > 0 ? "10px" : 0 },
            }}
          >
            <Typography noWrap sx={labelSx}>Review status</Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: `repeat(${review.length}, minmax(0, 1fr))`,
                  columnGap: 1,
                  alignItems: "stretch",
                }}
              >
                {review.map((status) => {
                  const share = (status.count / maxReview) * 100;
                  return (
                    <Tooltip key={status.value} title={`${status.label}: ${formatCount(status.count)}`} arrow placement="top">
                      <Box sx={{ minWidth: 0, height: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                        <Typography noWrap sx={{ ...metricValueSx, lineHeight: 1.2, color: status.count > 0 ? AH_TEXT : AH_MUTED }}>
                          {formatCount(status.count)}
                        </Typography>
                        <Box
                          sx={{
                            width: 28,
                            minWidth: 28,
                            maxWidth: 28,
                            height: 72,
                            minHeight: 72,
                            maxHeight: 72,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "flex-end",
                            bgcolor: AH_TRACK,
                            borderRadius: "4px 4px 0 0",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              height: `${share}%`,
                              minHeight: status.count > 0 ? 3 : 0,
                              bgcolor: status.color,
                              borderRadius: "4px 4px 0 0",
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            ...metricHeadSx,
                            color: AH_MUTED,
                            width: "100%",
                            minHeight: "2.4em",
                            lineHeight: 1.15,
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                          }}
                        >
                          {status.label}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
  );
};

/**
 * One account on one framework: compliance score, automated results, and manual review.
 * With `onToggle`, only the header shows until it is expanded; `children` expand with the metrics.
 */
const AuditAccountCard = ({
  scope,
  audit,
  resources,
  compliance,
  scanDate,
  accountNumber,
  accountSwitcher,
  metricsStatus = "ready",
  expanded = true,
  onToggle,
  children,
}) => {
  const bodyId = useId();
  const collapsible = typeof onToggle === "function";
  const open = !collapsible || expanded;
  const known = metricsStatus === "ready" && Boolean(scope);
  const unavailable =
    metricsStatus === "error"
      ? "Assessment metrics are unavailable"
      : metricsStatus === "ready" && !scope
        ? "Framework not assessed on this account"
        : "";
  const pendingScan = known && !scope.hasScanResult;
  const accounts = Array.isArray(audit?.accounts) ? audit.accounts : [];
  const account = accounts.find((item) => String(item?.accountNumber) === String(accountNumber));
  const accountName =
    account?.accountName && String(account.accountName) !== String(accountNumber) ? account.accountName : "";
  const accountViewLabel = accountName
    ? `${accountName}, ${accountNumber} View`
    : accountNumber
      ? `${accountNumber} View`
      : "Account View";
  const score = known && scope.hasScanResult ? scorePercent(scope.auditScore) : null;
  const baseline = known && scope.hasScanResult ? scorePercent(scope.baseScore) : null;
  const components = known ? readComponents(scope.components) : readComponents(null);
  const passed = components.autoPassed + components.manualPassed;
  const failed = components.autoFailed + components.manualFailed;
  const review = useMemo(
    () => reviewRows(known ? statusCountsForAccount(audit, accountNumber) : null),
    [known, audit, accountNumber]
  );

  const accountAction =
    accountSwitcher || (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          minHeight: 38,
          px: 1.5,
          borderRadius: "8px",
          backgroundColor: alpha(AH_ACCENT, 0.06),
          border: `1.5px solid ${alpha(AH_ACCENT, 0.55)}`,
          maxWidth: "100%",
        }}
      >
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: AH_TEXT, whiteSpace: "nowrap" }}>
          Account: {accountNumber}
        </Typography>
      </Box>
    );

  const metrics = (
    <Box sx={accountGridSx}>
      <ComplianceScoreCard
        score={score}
        baseline={baseline}
        passed={passed}
        failed={failed}
        known={known}
        unavailable={unavailable}
        pendingScan={pendingScan}
      />
      <Box sx={sideStackSx}>
        <AutomatedControlsCard
          passed={components.autoPassed}
          failed={components.autoFailed}
          known={known}
          pendingScan={pendingScan}
          resources={resources}
        />
        <ManualControlsCard components={components} known={known} review={review} />
      </Box>
    </Box>
  );

  const header = (
    <CardHeader
      eyebrow={accountViewLabel}
      compliance={compliance}
      meta={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: { xs: "wrap", md: "nowrap" },
            columnGap: 1.5,
            rowGap: 0.5,
            whiteSpace: "nowrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CalendarMonthIcon sx={{ color: AH_MUTED, fontSize: 14 }} />
            <span>Scan date: {scanDate ? formatDateTime(scanDate) : "—"}</span>
          </Box>
          {!open ? (
            <>
              <MetaSeparator />
              <span>
                Compliance Score:{" "}
                <Box component="span" sx={{ color: AH_TEXT, fontWeight: 700 }}>
                  {Number.isFinite(score) ? `${score.toFixed(2)}%` : dash}
                </Box>
              </span>
            </>
          ) : null}
        </Box>
      }
      action={
        collapsible ? (
          // Clicks in the switcher, including its popup, must not toggle the card.
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, width: "100%", minWidth: 0 }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", flex: "1 1 auto", minWidth: 0 }}>{accountAction}</Box>
            <Tooltip title={open ? "Hide details" : "Show details"} arrow placement="top">
              <IconButton
                size="small"
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={bodyId}
                aria-label={open ? "Hide assessment details" : "Show assessment details"}
                sx={{ flexShrink: 0, color: AH_MUTED, border: `1px solid ${AH_BORDER}`, bgcolor: AH_WHITE, "&:hover": { bgcolor: AH_TRACK, color: AH_TEXT } }}
              >
                <ExpandMoreRoundedIcon
                  fontSize="small"
                  sx={{ transition: "transform 0.2s ease", transform: open ? "rotate(180deg)" : "none" }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          accountAction
        )
      }
    />
  );

  if (!collapsible) {
    return (
      <CardShell sx={{ p: 1.5, gap: 1 }}>
        {header}
        {metrics}
      </CardShell>
    );
  }

  return (
    <CardShell sx={{ p: 1.5, gap: 0 }}>
      <Box onClick={onToggle} sx={{ cursor: "pointer" }}>
        {header}
      </Box>
      <Collapse in={open} timeout="auto" mountOnEnter id={bodyId}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {metrics}
          {children}
        </Box>
      </Collapse>
    </CardShell>
  );
};

export default AuditAccountCard;
